import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import BrandChip from '../components/BrandChip';
import Breadcrumbs from '../components/Breadcrumbs';
import Footer from '../components/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';
import { 
  CheckCircle, 
  ChevronDown, 
  ChevronUp, 
  Phone, 
  Mail, 
  ArrowRight,
  Waves,
  Hotel,
  Home,
  Building2,
  TreePalm
} from 'lucide-react';

const PoolCleaningRobotsPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const useCases = [
    {
      icon: Hotel,
      title: "Hotels & Resorts",
      description: "Keep your pool pristine 24/7 for guest satisfaction. Our commercial pool robots handle large pools up to 25m with automated scheduling.",
      link: "/commercial-pool-cleaning-robot-india"
    },
    {
      icon: Home,
      title: "Residential Pools",
      description: "Enjoy your pool without the maintenance hassle. Perfect for villas, farmhouses, and apartment complexes.",
      link: "/residential-pool-cleaning-robots"
    },
    {
      icon: Building2,
      title: "Clubs & Sports Facilities",
      description: "Olympic-size pools, lap pools, and competition pools. Heavy-duty robots designed for high-traffic facilities.",
      link: "/commercial-pool-cleaning-robot-india"
    },
    {
      icon: TreePalm,
      title: "Wellness & Spas",
      description: "Hygiene-focused pool cleaning for wellness centers, spas, and therapeutic pools with gentle cleaning modes.",
      link: "/commercial-pool-cleaning-robot-india"
    }
  ];

  const products = [
    {
      name: "Robo Dressel",
      slug: "robo-dressel",
      price: "₹2,89,990",
      image: "https://customer-assets-jt897jd0.emergentagent.net/job_582314cb-e0d4-4f3b-95a4-147554a5a708/artifacts/3y07x1gj_aee2706b-9652-4a2b-8c52-5c06900c5667.png",
      imgClass: "object-cover",
      features: ["3500 sq.ft. coverage", "Quad active brushing", "Floor, walls & stairs", "2-year warranty"],
      badge: ""
    },
    {
      name: "Robo Titan",
      slug: "robo-titan",
      price: "₹4,50,000",
      image: "https://customer-assets.emergentagent.com/job_smart-living-18/artifacts/y34dz1wb_PHOTO-2025-12-24-16-57-25.jpg",
      features: ["1800m² coverage", "350W power", "Twin drive motors", "Commercial grade"],
      badge: ""
    }
  ];

  const faqs = [
    {
      question: "What is a robotic pool cleaner and how does it work?",
      answer: "A robotic pool cleaner is an autonomous device that cleans swimming pools without manual intervention. It uses electric motors to move around the pool, brushes to scrub surfaces, and a built-in filtration system to capture debris. Advanced models like Harpalyk pool robots use smart navigation to map the pool and ensure complete coverage of floors, walls, and waterline."
    },
    {
      question: "How much does a robotic pool cleaner cost in India?",
      answer: "Robotic pool cleaners in India range from ₹1,50,000 for basic residential models to ₹8,00,000+ for commercial-grade robots. Harpalyk offers residential pool robots starting at ₹2,89,990 and commercial pool robots from ₹4,50,000. EMI options are available starting at ₹15,000/month."
    },
    {
      question: "Can robotic pool cleaners clean walls and waterline?",
      answer: "Yes, advanced robotic pool cleaners like Harpalyk models feature wall-climbing capability and waterline cleaning. They use specialized brushes and suction to clean vertical surfaces and the tile line where algae and scum typically accumulate. This provides complete pool cleaning without manual scrubbing."
    },
    {
      question: "How long does a robotic pool cleaner take to clean a pool?",
      answer: "Cleaning time depends on pool size. For a standard residential pool (15-20m), a robotic cleaner typically takes 2-3 hours for a complete cycle including floor, walls, and waterline. Commercial pools may require 3-4 hours. Most models can be programmed for automatic cleaning cycles while you're away."
    },
    {
      question: "Is a robotic pool cleaner better than manual cleaning?",
      answer: "Robotic pool cleaners offer significant advantages over manual cleaning: 70% reduction in labor costs, consistent cleaning quality, 30% savings on chemicals, ability to clean at night, and no supervision required. For hotels and commercial properties, the ROI is typically achieved within 6-12 months."
    }
  ];

  // Schema markup
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  };

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Harpalyk Robotic Pool Cleaners",
    "description": "Advanced robotic pool cleaners for residential and commercial swimming pools in India",
    "brand": { "@type": "Brand", "name": "Harpalyk" },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "INR",
      "lowPrice": "289990",
      "highPrice": "450000"
    }
  };

  return (
    <>
      <Helmet>
        <title>POOLBOT — Intelligent Pool Cleaning Robots | Harpalyk India</title>
        <meta name="description" content="Automatic pool cleaning robots that scrub floors, walls & waterline — for villas, hotels & resorts across India. From ₹2,89,990 with free demo, EMI & 2-year warranty." />
        <meta name="keywords" content="robotic pool cleaner india, pool cleaning robot, automatic pool cleaner, swimming pool robot, commercial pool robot, residential pool cleaner" />
        <link rel="canonical" href="https://harpalyk.com/pool-cleaning-robots" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      </Helmet>

      <Header />

      <main className="pt-20">
        <div className="h-1 bg-gradient-to-r from-blue-500 via-blue-400 to-transparent" data-testid="family-accent-bar-poolbot" aria-hidden="true" />
        <Breadcrumbs items={[{ label: 'Pool Cleaning Robots' }]} />
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-900 text-white py-20 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block bg-cyan-500/20 text-cyan-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  HARPALYK · POOLBOT FAMILY
                </span>
                <h1 className="text-4xl lg:text-5xl font-bold leading-tight mb-6">
                  POOLBOT — Intelligent Pool Cleaning Robots
                </h1>
                <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                  Advanced <strong>robotic pool cleaners</strong> that automatically clean your swimming pool floor, walls, and waterline. 
                  Perfect for residential pools, hotels, resorts, and commercial facilities across India.
                </p>
                <div className="flex flex-wrap gap-4 mb-8">
                  <a href="#products" className="bg-cyan-500 hover:bg-cyan-400 text-white px-8 py-4 rounded-lg font-semibold flex items-center gap-2 transition-all">
                    View Pool Robots <ArrowRight className="w-5 h-5" />
                  </a>
                  <a href="tel:+917011370322" className="border-2 border-white/30 hover:border-white text-white px-8 py-4 rounded-lg font-semibold flex items-center gap-2 transition-all">
                    <Phone className="w-5 h-5" /> Call for Demo
                  </a>
                </div>
                
                <div className="flex flex-wrap gap-6 pt-8 border-t border-white/20">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-cyan-400">100+</p>
                    <p className="text-sm text-blue-200">Pools Cleaned</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-cyan-400">70%</p>
                    <p className="text-sm text-blue-200">Cost Savings</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-cyan-400">2 Year</p>
                    <p className="text-sm text-blue-200">Warranty</p>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=600&q=80" 
                  alt="Robotic Pool Cleaner India" 
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-blue-600 font-semibold uppercase tracking-wide text-sm">Technology</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">
                How Robotic Pool Cleaners Work
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Understanding the advanced technology behind automatic pool cleaning robots
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-6">
              {[
                { step: "1", title: "Smart Navigation", desc: "Advanced algorithms map your pool and plan the most efficient cleaning path" },
                { step: "2", title: "Powerful Suction", desc: "High-powered motors create strong suction to capture debris of all sizes" },
                { step: "3", title: "Active Brushing", desc: "Rotating brushes scrub pool surfaces to remove algae and stuck-on dirt" },
                { step: "4", title: "Built-in Filtration", desc: "Multi-stage filters capture fine particles, leaving water crystal clear" }
              ].map((item, index) => (
                <div key={index} className="text-center p-6">
                  <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-20 px-6 lg:px-12 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-blue-600 font-semibold uppercase tracking-wide text-sm">Applications</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">
                Pool Cleaning Robots for Every Application
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {useCases.map((useCase, index) => (
                <a key={index} href={useCase.link} className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-all border border-gray-100 group">
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 p-4 rounded-xl">
                      <useCase.icon className="w-8 h-8 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">{useCase.title}</h3>
                      <p className="text-gray-600">{useCase.description}</p>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Products */}
        <section id="products" className="py-20 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-blue-600 font-semibold uppercase tracking-wide text-sm">Our Products</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">
                Harpalyk Pool Cleaning Robots
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                New to pool automation? Start with our{' '}
                <a href="/blog/pool-cleaning-robot-buying-guide-india" className="text-blue-600 hover:text-blue-700 underline">
                  pool cleaning robot buying guide
                </a>.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {products.map((product, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
                  <div className={`relative overflow-hidden aspect-[4/3] bg-white`}>
                    <img src={product.image} alt={`Harpalyk POOLBOT ${product.name} — robotic pool cleaner`} className={`w-full h-full ${product.imgClass || 'object-contain'}`} />
                    <BrandChip family="POOLBOT" />
                    {product.badge && (
                      <span className="absolute top-4 left-4 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                        {product.badge}
                      </span>
                    )}
                  </div>
                  <div className="p-8">
                    <p className="text-[11px] font-semibold tracking-[0.18em] text-gray-400 uppercase mb-1">HARPALYK · POOLBOT</p>
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-2xl font-bold text-gray-900">{product.name}</h3>
                      <p className="text-2xl font-bold text-blue-600">{product.price}</p>
                    </div>
                    <ul className="space-y-2 mb-6">
                      {product.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-gray-600">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <a href={`/products/${product.slug}`} className="block text-center bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold transition-all">
                      View Details & Price
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Comparison */}
        <section className="py-20 px-6 lg:px-12 bg-blue-900 text-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Manual vs Robotic Pool Cleaning
              </h2>
              <p className="text-xl text-blue-200">
                See why property owners are switching to automatic pool cleaning robots
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full bg-white/10 backdrop-blur rounded-2xl overflow-hidden">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="px-6 py-4 text-left font-semibold">Aspect</th>
                    <th className="px-6 py-4 text-left font-semibold text-red-300">Manual Cleaning</th>
                    <th className="px-6 py-4 text-left font-semibold text-cyan-300">Robotic Cleaning</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Monthly Cost", "₹15,000–25,000", "One-time investment"],
                    ["Time Required", "4–6 hours daily", "2–3 hours (automated)"],
                    ["Coverage", "Often incomplete", "100% floor, walls & waterline"],
                    ["Consistency", "Variable", "Uniform results every time"],
                    ["Chemical Usage", "High", "30% reduction"],
                    ["Night Cleaning", "Not possible", "Programmable schedules"]
                  ].map((row, index) => (
                    <tr key={index} className="border-b border-white/10">
                      <td className="px-6 py-4 font-medium">{row[0]}</td>
                      <td className="px-6 py-4 text-red-300">{row[1]}</td>
                      <td className="px-6 py-4 text-cyan-300">{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="contact" className="py-20 px-6 lg:px-12 bg-gradient-to-br from-cyan-600 to-blue-700 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready to Automate Your Pool Cleaning?
            </h2>
            <p className="text-xl text-cyan-100 mb-8">
              Get a free consultation and demo of our robotic pool cleaners. 
              We'll help you choose the right model for your pool.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <a href="tel:+917011370322" className="bg-white text-blue-700 px-8 py-4 rounded-lg font-bold hover:bg-cyan-100 transition-all flex items-center gap-2">
                <Phone className="w-5 h-5" /> +91-7011370322
              </a>
              <a href="mailto:sales@harpalyk.com" className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold hover:bg-white/10 transition-all flex items-center gap-2">
                <Mail className="w-5 h-5" /> sales@harpalyk.com
              </a>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-blue-600 font-semibold uppercase tracking-wide text-sm">FAQs</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">
                Frequently Asked Questions About Pool Cleaning Robots
              </h2>
            </div>
            
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <button 
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-all"
                  >
                    <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppWidget />
    </>
  );
};

export default PoolCleaningRobotsPage;
